import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { useToast } from "@/components/ui/use-toast";
import {
  Module,
  PATH_ROOT,
  PATH_TEMPLATES,
  PRODUCT_PLACEHOLDER,
} from "@/constants";
import { ApiError } from "@/services/api";
import {
  fetchSurveyTemplate,
  updateSurveyTemplate,
} from "@/services/survey-templates";
import { ReadSurvey } from "@/types/survey";
import { useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SurveyForm from "./SurveyForm";
import AddQuestionButton from "./components/Toolbar/components/AddQuestionButton";
import ClearButton from "./components/Toolbar/components/ClearButton";
import SaveButton from "./components/Toolbar/components/SaveButton";
import { NotepadTextDashed } from "lucide-react";
import { WriteSurvey } from "@/types/survey";

const SurveyTemplateEditor = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const params = useParams();

  const surveyId = params.id as string;

  const querySurveyTemplate = () => fetchSurveyTemplate(params.id);

  const mutateSurveyTemplate = async (
    values: WriteSurvey,
  ): Promise<ReadSurvey> => {
    const response = await updateSurveyTemplate(surveyId, values);
    return response;
  };

  const query = useQuery({
    queryKey: [Module.SurveyTemplates, params.id],
    queryFn: querySurveyTemplate,
  });

  const mutation = useMutation<ReadSurvey, ApiError, WriteSurvey>({
    mutationFn: mutateSurveyTemplate,
    onSuccess: () => {
      toast({
        title: t("surveyEditor.success.updateSurveyTemplate"),
      });
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("surveyEditor.errors.updateSurveyTemplate"),
        description,
        variant: "destructive",
      });
    },
  });

  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("breadcrumb.surveyTemplates"), href: PATH_TEMPLATES },
      {
        label: t("breadcrumb.surveyTemplate", {
          name: query.data?.name,
        }),
      },
    ],
    [t, query.data?.name],
  );

  if (query.isLoading) {
    return (
      <Spinner
        label={t("surveyEditor.loadingSurveyTemplate")}
        className="w-full h-full "
      />
    );
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <SurveyForm
        isNew={false}
        values={query.data}
        busy={mutation.isPending}
        onSubmit={mutation.mutateAsync}
        icon={<NotepadTextDashed className="w-6 h-6 mr-3" />}
        title={t("surveyEditor.surveyTemplateTitle")}
        buttons={
          <>
            <SaveButton />
            <AddQuestionButton />
            <ClearButton />
          </>
        }
        interQuestionToolbar={(questionIndex) => (
          <AddQuestionButton
            insertAt={questionIndex}
            label={t("surveyEditor.toolbar.insertQuestion")}
          />
        )}
        hint={t("surveyEditor.hintForTemplates", {
          product: PRODUCT_PLACEHOLDER,
        })}
      />
    </>
  );
};

export default SurveyTemplateEditor;
